import { ReportingLibrary, ReportRequestTypeEnum } from '@lyearn-team/reporting-library';

import { SnackBarHelper } from '@lyearn-team/frontend-components';
import flatten from 'lodash/flatten';
import fetchContentVersions from '../fetchContentVersions';
import transformCourseForLearningSpace from './transformCourseForLearningSpace';
import fetchOfferLocallyOrRemotely, { fetchLearnerOfferRemotely } from './fetchOffer';
import { getContentInfoWithMissingStates } from './fixAnyErrorsInStates';
import switchToLatestVersion from '../switchToLatestVersion';
import { ContentTypesLowercaseEnum } from '../../appConsts';
import enrolUserContent from '../enrolUserContent';
import { user } from '../currentUser';
import isContentAssigned from '../isContentAssigned';
import { checkForValidLicenseType } from '../checkForValidLicenseType';
import { checkForContentAvailability } from '../checkForContentAvailability';
import { contentLockStatus } from '../contentLockStatus';
import CustomError from '../CustomError';

function getPathId() {
  if (!window.location.href.includes('/paths/')) {
    return '';
  }

  return window.location.href.split('/paths/')[1].split('/')[0];
}

// Logic:
// - Fetch course locally
// - If not found locally fetch remotely
//
// - Get isCourseAssigned from courseEnrolment
// - If not isCourseAssigned
//   - enrolCourse or enrolPath
//   - fetch course remotely
// - Else
//   - Fetch course versions
//   - If coruseEnrolment.versionId !== latest version
//     - Popup to switch to latest version
//       - call switchToLatestVersion
//       - fetch course remotely
//     - Else
//       - redirect to homepage
//   - Get missingStates
//   - If missingStates > 0
//     - fix missing states
//     - fetch course remotely
// - return transformted course
export default async function fetchLearnerCourse(courseId) {
  const isGuestMode = user.isGuest();
  const userId = isGuestMode ? '5e7de277beee070008721310' : user.id();
  const companyId = isGuestMode ? '56bfef41a17441a959d9c8da' : user.companyId();
  const pathId = getPathId();

  let offerData = await fetchOfferLocallyOrRemotely(courseId, userId);
  const offerEnrolment = offerData.enrolments.find(e => e.contentId === courseId);
  const offerContentSetting = offerData.contentSettings.find(e => e.contentId === courseId);

  // If course is deleted then redirect to home page.
  // Handled in learnerViewCard component
  const isOfferDeleted = !offerContentSetting || offerContentSetting.status === 'deleted';
  const isValidLicancedUser = isGuestMode
    ? true
    : checkForValidLicenseType(offerContentSetting.customProperties);
  const isContentLocked =
    offerEnrolment && (await contentLockStatus(courseId, offerEnrolment.parentIds));
  const isCourseAssigned = isContentAssigned(offerEnrolment);
  const curriculumEnrolment =
    pathId && isCourseAssigned
      ? flatten(offerData.enrolments.map(enrolment => enrolment.parentIds)).includes(pathId)
      : true;
  const { availableOn, isAvailable } = checkForContentAvailability(offerEnrolment);
  if (isOfferDeleted) {
    window.analytics &&
      window.analytics.track('DELETED_COURSE_REDIRECT_TO_LEARNER_PAGE', { courseId });
    throw new Error('DELETED_COURSE_REDIRECT_TO_LEARNER_PAGE');
  }

  if (!isValidLicancedUser && !isGuestMode) {
    window.analytics && window.analytics.track('INVALID_LICENSE_ACCESS', { courseId });
    throw new Error('INVALID_LICENSE_ACCESS');
  }

  if (!isAvailable && !isGuestMode) {
    window.analytics && window.analytics.track('CONTENT_NOT_AVAILABLE_ACCESS', { courseId });
    const error = new CustomError({
      message: `CONTENT_NOT_AVAILABLE_ACCESS dateAvailable*${availableOn}*`,
      property: { name: 'dateAvailable', value: availableOn }
    });
    throw error;
  }

  if (isContentLocked && !isGuestMode) {
    window.analytics && window.analytics.track('LOCKED_COURSE_ACCESS', { courseId });
    throw new Error('LOCKED_COURSE_ACCESS');
  }

  if (!isCourseAssigned || !curriculumEnrolment) {
    await enrolUserContent(pathId || courseId, userId, companyId, isGuestMode === 'true');
    offerData = await fetchLearnerOfferRemotely(courseId, userId);
  } else if (!isGuestMode) {
    const courseVersions = await fetchContentVersions(new ReportingLibrary(), courseId, {
      type: ReportRequestTypeEnum.PAGINATED,
      from: 0,
      size: 1
    });
    const currentVersionId = offerEnrolment.versionId;
    const latestVersionId = courseVersions.reports[0].contentVersionLog.versionId;
    if (currentVersionId !== latestVersionId) {
      await switchToLatestVersion(
        userId,
        companyId,
        courseId,
        ContentTypesLowercaseEnum.offer,
        currentVersionId,
        latestVersionId
      );
      offerData = await fetchLearnerOfferRemotely(courseId, userId);
      SnackBarHelper.showSnackbar({
        label: 'This course has been updated since you last visited',
        duration: 7
      });
    }

    const contentInfoWithMissingStates = getContentInfoWithMissingStates(courseId, offerData);
    if (contentInfoWithMissingStates.length > 0) {
      console.log('contentInfoWithMissingStates : ', contentInfoWithMissingStates);
    }
  }

  return transformCourseForLearningSpace(
    offerData.enrolments,
    offerData.offer,
    offerData.states,
    offerData.stateInteractions,
    offerData.contentDetails,
    offerData.preRequisitesData,
    offerContentSetting
  );
}
